import * as React from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Chip from '@mui/joy/Chip'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Tooltip from '@mui/joy/Tooltip'
import Typography from '@mui/joy/Typography'
import { SHADOW_ACCEPTED, SHADOW_COUNTED_AGAINST, ShadowFieldStatus, ShadowJobComparison } from '@numeo/types-core'
import { formatShadowValue, SHADOW_STATUS, SHADOW_STATUS_ORDER } from './status'

type Filter = 'against' | 'accepted' | 'all'

const FILTERS: Array<{ value: Filter; label: string }> = [
    { value: 'against', label: 'What differs' },
    { value: 'accepted', label: 'Accepted differences' },
    { value: 'all', label: 'Every field' },
]

const matches = (filter: Filter, status: ShadowFieldStatus) =>
    filter === 'all' || (filter === 'against' ? SHADOW_COUNTED_AGAINST.includes(status) : SHADOW_ACCEPTED.includes(status))

/**
 * What the legacy chain saved against what the pipeline extracted, field by field, with the judge's verdict on each: identical, the same fact
 * worded differently, a value only one side has, or a real conflict. Opens on what differs, or on everything when nothing does.
 */
export default function ShadowDeltaTable({ comparison }: { comparison: ShadowJobComparison }) {
    const [filter, setFilter] = React.useState<Filter>(() => (SHADOW_COUNTED_AGAINST.some((status) => comparison.counts[status]) ? 'against' : 'all'))

    if (!comparison.rateConfId) {
        return (
            <Alert color="neutral" variant="soft">
                The legacy chain saved no RateConf for this document (it recorded: {comparison.legacyState}), so there is nothing to compare.
            </Alert>
        )
    }
    if (!comparison.hasExtraction) {
        return (
            <Alert color="neutral" variant="soft">
                The pipeline&apos;s extraction of this document has expired from the cache, so there is nothing to compare with the legacy RateConf.
            </Alert>
        )
    }

    const shown = comparison.fields.filter((field) => matches(filter, field.status))
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {SHADOW_STATUS_ORDER.map((status) => (
                    <Tooltip key={status} title={SHADOW_STATUS[status].hint} placement="top">
                        <Chip size="sm" variant="soft" color={SHADOW_STATUS[status].tone}>
                            {SHADOW_STATUS[status].label}: {comparison.counts[status]}
                        </Chip>
                    </Tooltip>
                ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {FILTERS.map(({ value, label }) => (
                    <Button key={value} size="sm" variant={filter === value ? 'solid' : 'outlined'} color="neutral" onClick={() => setFilter(value)}>
                        {label}
                    </Button>
                ))}
            </Box>
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                <Table stickyHeader size="sm" sx={{ '--TableCell-headBackground': 'var(--joy-palette-background-level1)', tableLayout: 'fixed' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '27%' }}>Field</th>
                            <th style={{ width: '27%' }}>Legacy (saved)</th>
                            <th style={{ width: '27%' }}>Pipeline (extracted)</th>
                            <th style={{ width: '19%' }}>Verdict</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.length === 0 && (
                            <tr>
                                <td colSpan={4}>
                                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                        Nothing in this view.
                                    </Typography>
                                </td>
                            </tr>
                        )}
                        {shown.map((field) => (
                            <tr
                                key={field.path}
                                style={{
                                    background:
                                        field.status === ShadowFieldStatus.DIFFERS
                                            ? 'var(--joy-palette-danger-softBg)'
                                            : SHADOW_COUNTED_AGAINST.includes(field.status)
                                              ? 'var(--joy-palette-warning-softBg)'
                                              : undefined,
                                }}
                            >
                                <td style={{ wordBreak: 'break-word' }}>
                                    <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                        {field.path}
                                    </Typography>
                                </td>
                                <td style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{formatShadowValue(field.legacy)}</td>
                                <td style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{formatShadowValue(field.pipeline)}</td>
                                <td>
                                    <Chip size="sm" variant="soft" color={SHADOW_STATUS[field.status].tone}>
                                        {SHADOW_STATUS[field.status].label}
                                    </Chip>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Sheet>
        </Box>
    )
}
